/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import styled from "styled-components";
import {
  Button,
  Col,
  FlexContainer,
  ModalConfirm,
  Row,
  SVGIcon,
  Tab,
  Tabs,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  Maybe,
  useQueryAccountsV2ByProviderQuery,
  useQueryAccountsV2CountsQuery,
} from "~/generated/graphql";
import {
  BreadcrumbsBox,
  LoaderWrap,
  Pagination,
  ReactTable,
  UnstyledLink,
} from "~/components";
import { ErrorMessage } from "~/components/Error";
import { ERROR_TEXT } from "~/constants";
import { useQueryStateWithTabs, useScrollAfterPaging } from "~/hooks";
import { useRemoveAccount } from "~/hooks/graphql";
import { ROUTES } from "~/routing/routes";
import { showError, showSuccess } from "~/services/toast";
import {
  generateActionConfirmText,
  generateActionSuccessText,
  getIcon,
} from "~/tools";
import { AccountsListColumns } from "./AccountsList";
import { ACCOUNT_TYPE_LIST } from "./constants";
import { ACTION, initialState, reducer } from "./reducer";

const TabTitleStyle = styled.span`
  display: flex;
  flex-direction: row;
`;

type PaginationWithTabsState = {
  page: number;
  limit: number;
  tab: number;
};

export function AccountsPage() {
  const scrollToRef = React.useRef<HTMLDivElement>(null);

  const { state, updateQueryState } =
    useQueryStateWithTabs<PaginationWithTabsState>();
  const { page, limit, tab } = state;

  const refetchData = () => {
    void refectAccountsCount();
    void refetch();
  };

  React.useEffect(() => {
    updateQueryState({
      limit: 20,
      page: 0,
      tab,
    });
    // we want to run this effect only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, loading, refetch, error } = useQueryAccountsV2ByProviderQuery({
    variables: {
      limit,
      page,
      filter: ACCOUNT_TYPE_LIST[tab].filter,
    },
  });

  const { data: accountsCounts, refetch: refectAccountsCount } =
    useQueryAccountsV2CountsQuery();

  const [reducerState, dispatch] = React.useReducer(reducer, initialState);

  const [removeAccount] = useRemoveAccount();

  const onDelete = async () => {
    try {
      await removeAccount({ nid: reducerState.tempOrgUnitData.nid ?? "" });
      showSuccess(
        generateActionSuccessText(
          `The ${reducerState.tempOrgUnitData.name ?? "no name"}`
        )()("removed")()
      );
      refetchData();
      closeModal();
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  const closeModal = () => {
    dispatch({ type: ACTION.CLOSE_MODAL });
  };

  const onRemoveHandler = React.useCallback((id: string, name: string) => {
    dispatch({ type: ACTION.REMOVE, payload: { id, name } });
  }, []);

  const columns = React.useMemo(
    () => AccountsListColumns({ onRemoveHandler, activeTab: tab }),
    [onRemoveHandler, tab]
  );

  const sortBy = {
    sortBy: [
      {
        id: "name",
        desc: false,
      },
    ],
  };

  useScrollAfterPaging({ scrollToRef, enabled: !loading });

  const generateTab = (
    name: string,
    countFn: () => Maybe<number> | undefined
  ) => {
    const count = countFn() ?? 0;
    const label = name === "GCP" ? "Google Cloud" : name;

    return (
      <Tab
        label={
          <>
            <TabTitleStyle>
              <SVGIcon name={getIcon(name)} size="md" />
              <Text
                marginLeft={theme.spacing.spacing04}
                weight="medium"
                mb={theme.spacing.spacing01}
                data-testid={name}
              >
                {label} ({count})
              </Text>
            </TabTitleStyle>
          </>
        }
      >
        <div ref={scrollToRef}>
          <LoaderWrap loading={loading}>
            <ReactTable
              striped
              initialState={sortBy}
              columns={columns}
              data={data?.accountsV2?.accounts || []}
            />
            {count > 0 && <Pagination count={Number(count)} />}
          </LoaderWrap>
        </div>
      </Tab>
    );
  };

  return (
    <>
      <Row>
        <Col>
          <BreadcrumbsBox>
            <FlexContainer columnGap={theme.spacing.spacing04}>
              <UnstyledLink to={ROUTES.accounts.addBillingData}>
                <Button icon="plus">Add Billing Data</Button>
              </UnstyledLink>
              <UnstyledLink to={ROUTES.accounts.add}>
                <Button icon="plus">Add new Account</Button>
              </UnstyledLink>
            </FlexContainer>
          </BreadcrumbsBox>
          <ErrorMessage error={error} />
          <Tabs
            step={tab}
            handleTab={(activeTab) =>
              updateQueryState({
                ...state,
                tab: activeTab,
                page: 0,
              })
            }
          >
            {generateTab("AWS", () => accountsCounts?.AWS?.count)}
            {generateTab("AZURE", () => accountsCounts?.AZURE?.count)}
            {generateTab("GCP", () => accountsCounts?.GCP?.count)}
            {generateTab("VMware", () => accountsCounts?.VMWARE?.count)}
            {generateTab("Kubernetes", () => accountsCounts?.KUBERNETES?.count)}
            {generateTab("OpenShift", () => accountsCounts?.OPENSHIFT?.count)}
            {generateTab("IBMCloud", () => accountsCounts?.IBMCLOUD?.count)}
          </Tabs>
        </Col>
      </Row>
      <ModalConfirm
        isOpen={reducerState.isModalConfirmOpen}
        confirm={onDelete}
        actionLabel="Delete"
        contentLabel="Delete Account"
        onClose={closeModal}
      >
        {generateActionConfirmText(reducerState.tempOrgUnitData.name ?? "")(
          "delete"
        )()}
      </ModalConfirm>
    </>
  );
}
